import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchExams as fetchExamsApi, fetchExamSchedules, fetchScheduleResults, saveStudentResult, updateStudentResult, type ExamApiRecord, type ExamScheduleApiRecord, type StudentResultRecord } from '../../services/examApi';
import { fetchClasses, fetchClassSections, fetchClassSubjects, type ClassApiRecord, type SectionApiRecord, type ClassSubjectRecord } from '../../services/classApi';
import { fetchStudents, type StudentApiRecord } from '../../services/studentApi';
import type { ApiError } from '../../services/api';
import './MarksEntry.css';
import { useTranslation } from 'react-i18next';

type StudentMark = {
    id: string;
    student_name: string;
    roll_number: number;
    avatar_initials: string;
    max_marks: number;
    obtained_marks: number | null;
    is_passed: boolean | null;
    /** Existing result ID from API — null if not yet saved */
    result_id: string | null;
};

const PASS_PERCENT = 33;  // percentage threshold for pass
const AUTO_SAVE_DELAY = 5000; // 5 seconds

export default function MarksEntry() {
    const { t: _t } = useTranslation();
    /* ── Context gateway state ── */
    const [selExam, setSelExam] = useState('');
    const [selClass, setSelClass] = useState('');
    const [selSection, setSelSection] = useState('');
    const [selSubject, setSelSubject] = useState('');

    /* ── Dropdown options ── */
    const [exams, setExams] = useState<ExamApiRecord[]>([]);
    const [classes, setClasses] = useState<ClassApiRecord[]>([]);
    const [sections, setSections] = useState<SectionApiRecord[]>([]);
    const [subjects, setSubjects] = useState<ClassSubjectRecord[]>([]);
    const [dropdownsLoading, setDropdownsLoading] = useState(true);

    /* ── Schedule + Data state ── */
    const [schedule, setSchedule] = useState<ExamScheduleApiRecord | null>(null);
    const [students, setStudents] = useState<StudentMark[]>([]);
    const [loading, setLoading] = useState(false);
    const [globalMax, setGlobalMax] = useState(100);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState<string | null>(null);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');

    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const contextReady = selExam && selClass && selSubject;

    /* ── Load exams & classes on mount ── */
    useEffect(() => {
        (async () => {
            try {
                const [examsRes, classesRes] = await Promise.all([
                    fetchExamsApi({ perPage: 50, isActive: true }),
                    fetchClasses({ perPage: 100 }),
                ]);
                setExams(examsRes.data);
                setClasses(classesRes.data);
            } catch {
                // silent — dropdowns will remain empty
            } finally {
                setDropdownsLoading(false);
            }
        })();
    }, []);

    /* ── Cascade: load sections when class changes ── */
    useEffect(() => {
        setSections([]);
        setSelSection('');
        setSubjects([]);
        setSelSubject('');
        if (!selClass) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchClassSections(selClass, { perPage: 100 });
                if (!cancelled) setSections(res.data);
            } catch {
                if (!cancelled) setSections([]);
            }
        })();
        return () => { cancelled = true; };
    }, [selClass]);

    /* ── Cascade: load subjects when class (+section) changes ── */
    useEffect(() => {
        setSubjects([]);
        setSelSubject('');
        if (!selClass) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchClassSubjects(selClass, {
                    perPage: 100,
                    sectionId: selSection || undefined,
                });
                if (!cancelled) setSubjects(res.data);
            } catch {
                if (!cancelled) setSubjects([]);
            }
        })();
        return () => { cancelled = true; };
    }, [selClass, selSection]);

    /* ── Load schedule + students + existing results when context is complete ── */
    useEffect(() => {
        if (!contextReady) {
            setStudents([]);
            setSchedule(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setErrorMsg('');
        (async () => {
            try {
                // 1. Find the exam schedule matching class + section + subject
                const schedulesRes = await fetchExamSchedules(selExam, { perPage: 200 });
                console.log('[MarksEntry] Matching schedule:', {
                    selClass, selSection, selSubject,
                    schedules: schedulesRes.data.map((s) => ({
                        id: s.id,
                        class_id: s.class_id,
                        section_id: s.section_id,
                        school_subject_id: s.school_subject_id,
                    })),
                });
                const matched = schedulesRes.data.find(
                    (s) => s.class_id === selClass &&
                        (!selSection || s.section_id === selSection) &&
                        s.school_subject_id === selSubject
                );
                if (cancelled) return;

                if (!matched) {
                    setSchedule(null);
                    setStudents([]);
                    setErrorMsg('No exam schedule found for the selected combination.');
                    setLoading(false);
                    return;
                }

                setSchedule(matched);
                const maxMarks = Number(matched.max_marks) || 100;
                const passMarks = Number(matched.pass_marks) || 33;
                setGlobalMax(maxMarks);

                // 2. Fetch students and existing results in parallel
                const [studentsRes, resultsRes] = await Promise.all([
                    fetchStudents({ perPage: 200, classId: selClass, sectionId: selSection }),
                    fetchScheduleResults(selExam, matched.id, { perPage: 200 }),
                ]);
                if (cancelled) return;

                // Build lookup: student_id → result record
                const resultsByStudent = new Map<string, StudentResultRecord>();
                for (const r of resultsRes.data) {
                    resultsByStudent.set(r.student_id, r);
                }

                // 3. Merge students with existing results
                const mapped: StudentMark[] = studentsRes.data.map((s: StudentApiRecord, idx: number) => {
                    const firstName = s.first_name ?? s.user?.first_name ?? '';
                    const lastName = s.last_name ?? s.user?.last_name ?? '';
                    const fullName = s.name ?? `${firstName}${lastName ? ' ' + lastName : ''}`.trim();
                    const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '?';
                    const existing = resultsByStudent.get(s.id);
                    const obtained = existing?.obtained_marks ?? null;
                    const isPassed = obtained !== null && obtained >= 0
                        ? (obtained / maxMarks) * 100 >= (passMarks / maxMarks) * 100
                        : null;
                    return {
                        id: s.id,
                        student_name: fullName,
                        roll_number: s.roll_number ? Number(s.roll_number) : idx + 1,
                        avatar_initials: initials,
                        max_marks: maxMarks,
                        obtained_marks: obtained,
                        is_passed: isPassed,
                        result_id: existing?.id ?? null,
                    };
                });
                setStudents(mapped);
                setDirty(false);
                setLastSaved(null);
            } catch {
                if (!cancelled) {
                    setStudents([]);
                    setErrorMsg('Failed to load data. Please try again.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selExam, selClass, selSection, selSubject]);

    /* ── Auto-save (debounced 5s) ── */
    useEffect(() => {
        if (!dirty) return;
        if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = setTimeout(() => {
            handleSave(true);
        }, AUTO_SAVE_DELAY);
        return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dirty, students]);

    /* ── Compute pass/fail live ── */
    const computePass = useCallback((obtained: number | null, max: number): boolean | null => {
        if (obtained === null || obtained < 0) return null;
        return (obtained / max) * 100 >= PASS_PERCENT;
    }, []);

    /* ── Update obtained marks for a student ── */
    const updateMarks = (idx: number, rawValue: string) => {
        const val = rawValue === '' ? null : Number(rawValue);
        setStudents((prev) =>
            prev.map((s, i) => {
                if (i !== idx) return s;
                const obtained = val !== null && val < 0 ? 0 : val;
                return { ...s, obtained_marks: obtained, is_passed: computePass(obtained, s.max_marks) };
            })
        );
        setDirty(true);
    };

    /* ── Update global max marks ── */
    const updateGlobalMax = (val: number) => {
        const max = Math.max(1, val);
        setGlobalMax(max);
        setStudents((prev) =>
            prev.map((s) => ({
                ...s,
                max_marks: max,
                is_passed: computePass(s.obtained_marks, max),
            }))
        );
        setDirty(true);
    };

    /* ── Save all marks via API ── */
    const handleSave = async (isAuto = false) => {
        if (saving || !schedule) return;
        setSaving(true);
        setErrorMsg('');

        // Only save students who have marks entered
        const toSave = students.filter((s) => s.obtained_marks !== null);
        let savedCount = 0;
        let failedCount = 0;

        for (const student of toSave) {
            try {
                let result: StudentResultRecord;
                if (student.result_id) {
                    // Update existing result
                    result = await updateStudentResult(student.result_id, {
                        obtained_marks: student.obtained_marks,
                    });
                } else {
                    // Create new result
                    result = await saveStudentResult(selExam, schedule.id, {
                        student_id: student.id,
                        obtained_marks: student.obtained_marks,
                    });
                }
                // Store the result_id for future updates
                setStudents((prev) => prev.map((s) =>
                    s.id === student.id ? { ...s, result_id: result.id } : s
                ));
                savedCount++;
            } catch (err) {
                const apiErr = err as ApiError;
                // Skip duplicate errors (already saved) — treat as success
                if (apiErr.errors?.student_id) {
                    savedCount++;
                } else {
                    failedCount++;
                }
            }
        }

        setSaving(false);
        setDirty(false);
        const now = new Date();
        setLastSaved(now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));

        if (failedCount > 0) {
            setErrorMsg(`${savedCount} saved, ${failedCount} failed. Please retry.`);
        } else if (!isAuto && savedCount > 0) {
            setSuccessMsg(`${savedCount} mark${savedCount > 1 ? 's' : ''} saved successfully!`);
            setTimeout(() => setSuccessMsg(''), 5000);
        }
    };

    /* ── Keyboard navigation ── */
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
        const move = (target: number) => {
            e.preventDefault();
            inputRefs.current[target]?.focus();
            inputRefs.current[target]?.select();
        };
        if (e.key === 'ArrowDown' || e.key === 'Enter') {
            if (idx < students.length - 1) move(idx + 1);
        } else if (e.key === 'ArrowUp') {
            if (idx > 0) move(idx - 1);
        }
        // Tab already handled natively
    };

    /* ── Stats ── */
    const filled = students.filter((s) => s.obtained_marks !== null).length;
    const passed = students.filter((s) => s.is_passed === true).length;
    const failed = students.filter((s) => s.is_passed === false).length;

    return (
        <div className="marks">
            {successMsg && (
                <div className="marks__toast">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                    <span>{successMsg}</span>
                    <button className="marks__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}
            {errorMsg && (
                <div className="marks__toast marks__toast--error">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                    {errorMsg}
                    <button className="marks__toast-dismiss" onClick={() => setErrorMsg('')}>&times;</button>
                </div>
            )}

            <div className="marks__header">
                <div className="marks__header-text">
                    <h1 className="marks__title">Marks Entry</h1>
                    <p className="marks__subtitle">Enter student marks in a spreadsheet-like grid.</p>
                </div>
            </div>

            {/* Context Gateway */}
            <div className="marks__context-card">
                <div className="marks__context-grid">
                    <div className="marks__context-field">
                        <label className="marks__context-label" htmlFor="ctxExam">Exam</label>
                        <select className="marks__context-select" id="ctxExam" value={selExam} disabled={dropdownsLoading} onChange={(e) => setSelExam(e.target.value)}>
                            <option value="">Select Exam…</option>
                            {exams.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                        </select>
                    </div>
                    <div className="marks__context-field">
                        <label className="marks__context-label" htmlFor="ctxClass">Class</label>
                        <select className="marks__context-select" id="ctxClass" value={selClass} disabled={dropdownsLoading} onChange={(e) => setSelClass(e.target.value)}>
                            <option value="">Select Class…</option>
                            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    <div className="marks__context-field">
                        <label className="marks__context-label" htmlFor="ctxSection">Section</label>
                        <select className="marks__context-select" id="ctxSection" value={selSection} disabled={!selClass} onChange={(e) => setSelSection(e.target.value)}>
                            <option value="">All Sections</option>
                            {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                    </div>
                    <div className="marks__context-field">
                        <label className="marks__context-label" htmlFor="ctxSubject">Subject</label>
                        <select className="marks__context-select" id="ctxSubject" value={selSubject} disabled={!selClass} onChange={(e) => setSelSubject(e.target.value)}>
                            <option value="">Select Subject…</option>
                            {subjects.map((s) => <option key={s.id} value={s.school_subject_id}>{s.subject_name || s.board_subject?.name || '—'}</option>)}
                        </select>
                    </div>
                </div>
            </div>

            {/* Body */}
            {!contextReady ? (
                <div className="marks__empty-state">
                    <div className="marks__empty-icon">
                        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="18" height="18" rx="2" />
                            <path d="M3 9h18" />
                            <path d="M9 21V9" />
                        </svg>
                    </div>
                    <h3 className="marks__empty-title">Select Context to Begin</h3>
                    <p className="marks__empty-text">Choose an Exam, Class, and Subject from the dropdowns above to load the marks entry grid. Section is optional.</p>
                </div>
            ) : loading ? (
                <div className="marks__loading"><div className="marks__loader"><span className="marks__loader-dot" /><span className="marks__loader-dot" /><span className="marks__loader-dot" /></div></div>
            ) : (
                <>
                    {/* Stats bar */}
                    <div className="marks__stats-bar">
                        <div className="marks__stat">
                            <span className="marks__stat-value">{students.length}</span>
                            <span className="marks__stat-label">Students</span>
                        </div>
                        <div className="marks__stat">
                            <span className="marks__stat-value">{filled}</span>
                            <span className="marks__stat-label">Entered</span>
                        </div>
                        <div className="marks__stat marks__stat--pass">
                            <span className="marks__stat-value">{passed}</span>
                            <span className="marks__stat-label">Passed</span>
                        </div>
                        <div className="marks__stat marks__stat--fail">
                            <span className="marks__stat-value">{failed}</span>
                            <span className="marks__stat-label">Failed</span>
                        </div>
                        <div className="marks__stat-spacer" />
                        {lastSaved && <span className="marks__last-saved">Last saved: {lastSaved}</span>}
                        {dirty && <span className="marks__unsaved-dot" title="Unsaved changes" />}
                    </div>

                    {/* Spreadsheet grid */}
                    <div className="marks__grid-wrap">
                        <table className="marks__table">
                            <thead>
                                <tr>
                                    <th className="marks__th marks__th--roll">Roll No</th>
                                    <th className="marks__th marks__th--name">Student Name</th>
                                    <th className="marks__th marks__th--max">
                                        <div className="marks__max-header">
                                            <span>Max Marks</span>
                                            <input
                                                className="marks__max-input"
                                                type="number"
                                                min={1}
                                                value={globalMax}
                                                onChange={(e) => updateGlobalMax(Number(e.target.value))}
                                                title="Set max marks for all students"
                                            />
                                        </div>
                                    </th>
                                    <th className="marks__th marks__th--obtained">Obtained Marks</th>
                                    <th className="marks__th marks__th--status">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {students.map((s, idx) => {
                                    const isOverMax = s.obtained_marks !== null && s.obtained_marks > s.max_marks;
                                    return (
                                        <tr className="marks__row" key={s.id}>
                                            <td className="marks__td marks__td--roll">{s.roll_number}</td>
                                            <td className="marks__td marks__td--name">
                                                <div className="marks__student">
                                                    <span className="marks__avatar">{s.avatar_initials}</span>
                                                    <span className="marks__student-name">{s.student_name}</span>
                                                </div>
                                            </td>
                                            <td className="marks__td marks__td--max">{s.max_marks}</td>
                                            <td className="marks__td marks__td--obtained">
                                                <input
                                                    ref={(el) => { inputRefs.current[idx] = el; }}
                                                    className={`marks__marks-input ${isOverMax ? 'marks__marks-input--error' : ''}`}
                                                    type="number"
                                                    min={0}
                                                    max={s.max_marks}
                                                    value={s.obtained_marks !== null ? s.obtained_marks : ''}
                                                    placeholder="—"
                                                    onChange={(e) => updateMarks(idx, e.target.value)}
                                                    onKeyDown={(e) => handleKeyDown(e, idx)}
                                                />
                                            </td>
                                            <td className="marks__td marks__td--status">
                                                {s.is_passed === true && <span className="marks__badge marks__badge--pass">Pass</span>}
                                                {s.is_passed === false && <span className="marks__badge marks__badge--fail">Fail</span>}
                                                {s.is_passed === null && <span className="marks__badge marks__badge--pending">—</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Sticky save bar */}
                    <div className="marks__save-bar">
                        <button
                            className="marks__save-btn"
                            onClick={() => handleSave(false)}
                            disabled={saving || !dirty}
                        >
                            {saving ? (
                                <>
                                    <svg className="marks__spinner" width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" strokeWidth="3" /></svg>
                                    Saving…
                                </>
                            ) : (
                                <>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>
                                    Save All Marks
                                </>
                            )}
                        </button>
                        <span className="marks__save-hint">
                            {dirty ? 'You have unsaved changes' : 'All changes saved'}
                            {' · Auto-saves every 5 seconds'}
                        </span>
                    </div>
                </>
            )}
        </div>
    );
}
